import { fetchTopicLessonsData } from './dataApi';

// Loads {code}/topwords/topwordsN.json under data-topic-lessons — see
// data-topic-lessons/{code}/topwords/ in the assets repo. Only a subset of
// languages have this content so far (the same set that has topic-lessons
// content); loadTopWords returns an empty array rather than throwing when a
// language isn't covered yet, so the page can show a plain "not available
// for this language yet" state instead of an error. Mirrors the mobile
// app's TopWordsService (lib/features/top_words/data/top_words_service.dart)
// field-for-field.
const FALLBACK_NATIVE_CODE = 'en';

async function loadChunk(languageCode, chunk, { force = false } = {}) {
  const data = await fetchTopicLessonsData(`${languageCode}/topwords/topwords${chunk}.json`, { force });
  return Array.isArray(data?.items)
    ? data.items.filter((item) => item && typeof item === 'object')
    : [];
}

// Chunk 1 (topwords1.json) always exists once a language is covered; later
// chunks (topwords2.json, topwords3.json, ...) extend the same language
// file-by-file as the word count grows — a missing later chunk just means
// "that many words aren't authored yet", not an error.
async function loadAllChunksSafely(languageCode, { force = false } = {}) {
  try {
    const first = await loadChunk(languageCode, 1, { force });
    const items = [...first];
    let chunk = 2;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      try {
        items.push(...await loadChunk(languageCode, chunk, { force }));
        chunk += 1;
      } catch {
        break;
      }
    }
    return items;
  } catch {
    return [];
  }
}

// Pairs each of targetItems with its meaning from nativeItems at the same
// stable id, sorted by order. Pure and side-effect-free so it's unit
// testable without a network fetch — mirrors TopWordsService.mergeEntries.
export function mergeTopWordsEntries(targetItems, nativeItems) {
  const nativeById = new Map(nativeItems.map((item) => [String(item.id), item]));
  return targetItems
    .map((item) => {
      const id = String(item.id);
      const native = nativeById.get(id);
      return {
        id,
        text: item.text != null ? String(item.text) : '',
        meaning: native?.text != null ? String(native.text) : '',
        partOfSpeech: item.partOfSpeech != null ? String(item.partOfSpeech) : '',
        emoji: item.emoji != null ? String(item.emoji) : '',
        order: Number.isFinite(Number(item.order)) ? Number(item.order) : 0,
      };
    })
    .sort((a, b) => a.order - b.order);
}

// Loads targetCode's Top Words with each word's meaning resolved from
// nativeCode's own file at the same id, falling back to English if the
// native language doesn't have Top Words content. Never throws — an
// unsupported targetCode simply resolves to an empty array.
export async function loadTopWords(targetCode, nativeCode, { force = false } = {}) {
  const targetItems = await loadAllChunksSafely(targetCode, { force });
  if (targetItems.length === 0) return [];

  let nativeItems = nativeCode === targetCode
    ? targetItems
    : await loadAllChunksSafely(nativeCode, { force });
  if (nativeItems.length === 0 && nativeCode !== FALLBACK_NATIVE_CODE) {
    nativeItems = await loadAllChunksSafely(FALLBACK_NATIVE_CODE, { force });
  }
  return mergeTopWordsEntries(targetItems, nativeItems);
}
